import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { CLINIC } from '../data/clinic';
import { useLang } from '../i18n';
import { pathFor, altPath } from '../data/routes';
import OpenStatus from './OpenStatus';

const LINK_KEYS = ['home', 'walkin', 'pi', 'services', 'decompression', 'programs', 'technology', 'contact'];
const DESKTOP_LINKS = ['decompression', 'walkin', 'pi', 'services', 'technology', 'contact'];

const LangToggle = ({ lang, onSelect, testId }) => (
  <div className="flex items-center gap-1 font-body" data-testid={testId} style={{ fontSize: '14px', letterSpacing: '0.08em' }}>
    <button onClick={() => onSelect('en')} data-testid="lang-en" style={{ fontWeight: lang === 'en' ? 700 : 400, color: lang === 'en' ? '#1E4E8C' : '#A79E95' }}>EN</button>
    <span style={{ color: '#E6DFD4' }}>|</span>
    <button onClick={() => onSelect('es')} data-testid="lang-es" style={{ fontWeight: lang === 'es' ? 700 : 400, color: lang === 'es' ? '#1E4E8C' : '#A79E95' }}>ES</button>
  </div>
);

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useLang();

  const selectLang = (l) => { if (l !== lang) navigate(altPath(location.pathname, l)); };

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-nav"
        className="fixed top-0 left-0 right-0 z-50"
        style={{ background: '#F2F0ED', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderBottom: '1px solid #E6DFD4', boxShadow: scrolled ? '0 6px 24px rgba(26,23,20,0.08)' : 'none', transition: 'box-shadow 0.4s ease' }}
      >
        <div
          className="mx-auto flex w-full max-w-[1680px] items-center justify-between gap-4 px-6 md:px-10"
          style={{ height: scrolled ? '60px' : '76px', transition: 'height 0.4s cubic-bezier(0.22,1,0.36,1)' }}
        >
          <Link to={pathFor('home', lang)} data-testid="nav-brand" className="flex flex-col leading-none shrink-0 min-w-0">
            <span className="font-display whitespace-nowrap" style={{ fontSize: scrolled ? '15px' : 'clamp(14px, 3.6vw, 18px)', fontWeight: 700, color: '#1B1815', transition: 'font-size 0.4s ease' }}>
              {CLINIC.name.toUpperCase()}
            </span>
            <span
              className="font-body whitespace-nowrap overflow-hidden"
              style={{ fontSize: '15px', letterSpacing: '0.12em', color: '#A79E95', maxHeight: scrolled ? '0px' : '20px', opacity: scrolled ? 0 : 1, marginTop: scrolled ? '0px' : '4px', transition: 'max-height 0.4s ease, opacity 0.3s ease, margin-top 0.4s ease' }}
            >
              {CLINIC.tagline}
            </span>
          </Link>

          <nav className="hidden xl:flex items-center justify-center gap-x-5">
            {DESKTOP_LINKS.map((key) => {
              const to = pathFor(key, lang);
              const active = location.pathname === to;
              return (
                <Link
                  key={key}
                  to={to}
                  data-testid={`nav-link-${key}`}
                  className="font-body uppercase whitespace-nowrap transition-colors hover:text-[#1E4E8C]"
                  style={{ fontSize: '13px', letterSpacing: '0.06em', fontWeight: 600, color: active ? '#1E4E8C' : '#1B1815' }}
                >
                  {t.nav[key]}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-4 shrink-0">
            <OpenStatus compact className="hidden lg:inline-flex" />
            <a href={CLINIC.bookingUrl} target="_blank" rel="noreferrer" className="btn-primary !hidden sm:!inline-flex" data-testid="nav-book-btn" style={{ padding: '12px 24px', fontSize: '14px' }}>{t.nav.book}</a>
            <button className="xl:hidden flex items-center justify-center rounded-md p-1.5" data-testid="nav-hamburger" aria-label="Open menu" onClick={() => setOpen(true)} style={{ color: '#1B1815', border: '1px solid #E6DFD4', background: '#FFFFFF' }}>
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col" style={{ background: '#F2F0ED' }}
          >
            <div className="flex items-center justify-between px-6 h-[76px]" style={{ borderBottom: '1px solid #E6DFD4' }}>
              <span className="font-display" style={{ fontSize: '18px', fontWeight: 700 }}>{CLINIC.name.toUpperCase()}</span>
              <div className="flex items-center gap-5">
                <button data-testid="mobile-menu-close" aria-label="Close menu" onClick={() => setOpen(false)}><X size={30} /></button>
              </div>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-6 px-8 overflow-y-auto py-10">
              {LINK_KEYS.map((key, i) => {
                const to = pathFor(key, lang);
                return (
                  <motion.div key={key} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, duration: 0.4 }}>
                    <Link to={to} data-testid={`mobile-nav-link-${key}`} className="font-display block" style={{ fontSize: '36px', fontWeight: 600, color: location.pathname === to ? '#1E4E8C' : '#1B1815' }}>
                      {t.nav[key]}
                    </Link>
                  </motion.div>
                );
              })}
              <a href={CLINIC.bookingUrl} target="_blank" rel="noreferrer" className="btn-primary mt-6 inline-flex w-fit" data-testid="mobile-book-btn">{t.common.book}</a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
